import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigation, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  Button,
  DisplayText,
  IconLink,
  QRCodeScanner,
  ScrollViewWithHeaderTitle,
  theme,
} from '@sg/garnish';

import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import {
  AccountScreenTitle,
  CreditOverviewPromoCodeForm,
  TransparentModal,
  useHeaderRight,
} from '../../components';
import { usePromoCode } from '../../hooks';

export const CreditAddScreen = () => {
  const { formatMessage } = useIntl();

  const title = formatMessage(messages.accountScanToPayEnterPromoCode);

  const {
    promoCode,
    promoCodeInvalid,
    isPromoCodeApplied,
    isSubmittingPromoCode,
    setPromoCode,
    submitPromoCode,
  } = usePromoCode();
  const navigation = useNavigation();
  const router = useRouter();

  const [isScanning, setIsScanning] = useState(false);

  // ─────────────────────── Telemetry ──────────────────────────────────

  const { track } = useTelemetry();

  useTrackEffect('payment_credit.view_promo_code');

  const onMonitoredCameraShown = useCallback(() => {
    track('payment_credit.camera_opened');
  }, [track]);

  // ─────────────────────── Callbacks ──────────────────────────────────

  const handleShowScanner = useCallback(() => {
    onMonitoredCameraShown();
    setIsScanning(true);
  }, [onMonitoredCameraShown]);

  const handleHideScanner = useCallback(() => {
    setIsScanning(false);
  }, []);

  const handleGoBack = useCallback(() => {
    setPromoCode('');

    router.back();
  }, [router, setPromoCode]);

  const onPromoCodeScanned = useCallback(
    (code: string) => {
      setPromoCode(code);
      setIsScanning(false);
    },
    [setIsScanning, setPromoCode],
  );

  // ─────────────────────── Navigation ─────────────────────────────────

  const navigationHeaderRightItems = useMemo<NavigationHeaderRightItems>(
    () => [
      {
        key: 'account.gift-code.header.close-btn',
        icon: 'IconClose',
        accessibilityLabel: formatMessage(messages.creditAddClose),
        onPress: handleGoBack,
      },
    ],
    [formatMessage, handleGoBack],
  );
  const navigationHeaderRight = useHeaderRight({
    items: navigationHeaderRightItems,
  });

  // removes the header when scanning.
  const setOptions = navigation.setOptions;

  useLayoutEffect(() => {
    setOptions({
      headerShown: !isScanning,
      headerRight: navigationHeaderRight,
    });
  }, [isScanning, setOptions, navigationHeaderRight]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal size="full" onClose={handleGoBack} disableAnimation>
      {isScanning ? (
        <>
          <FloatingCloseScanIcon onClose={handleHideScanner} />
          <ScannerComponent
            onPromoCodeScanned={onPromoCodeScanned}
            handleHideScanner={handleHideScanner}
          />
        </>
      ) : (
        <ScrollViewWithHeaderTitle testID="account.gift-code.root.container">
          <View style={styles.padding}>
            <AccountScreenTitle title={title} />
            <CreditOverviewPromoCodeForm
              buttonPlacement="bottom"
              hasPromoCodeError={promoCodeInvalid}
              promoCode={promoCode}
              isPromoCodeApplied={isPromoCodeApplied}
              isSubmittingPromoCode={isSubmittingPromoCode}
              onPressApplyPromoCode={submitPromoCode}
              setPromoCode={setPromoCode}
              handleShowScanner={handleShowScanner}
            />
          </View>
        </ScrollViewWithHeaderTitle>
      )}
    </TransparentModal>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const ScannerComponent = (props: PromoCodeScannerProps) => {
  const { handleHideScanner, onPromoCodeScanned } = props;

  const { formatMessage } = useIntl();
  const [requestPermissionResponse, onRequestPermissionResponse] = useState(false);

  return (
    <View style={styles.scanner}>
      <QRCodeScanner
        onRequestPermissionResponse={onRequestPermissionResponse}
        onScanSuccess={onPromoCodeScanned}
      />
      <View style={styles.scannerMessageContainer}>
        <DisplayText style={styles.text} size={4}>
          {formatMessage(
            requestPermissionResponse
              ? messages.accountScanToPayScanGiftCodeMessage
              : messages.accountScanToPayScanGiftCodeCameraPermissionMessage,
          )}
        </DisplayText>

        <Button size="large" onPress={handleHideScanner}>
          {formatMessage(messages.creditAddCancel)}
        </Button>
      </View>
    </View>
  );
};

const FloatingCloseScanIcon = ({
  onClose,
}: Readonly<{
  onClose: () => void;
}>) => {
  const { formatMessage } = useIntl();

  return (
    <IconLink
      width={24}
      height={24}
      name="IconClose"
      testID="qr-code.scanner.close"
      color={theme.colors.GREEN_1}
      accessibilityLabel={formatMessage(messages.creditAddClose)}
      onPress={onClose}
      style={styles.floatingButton}
    />
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountScanToPayEnterPromoCode: {
    defaultMessage: 'Enter promo code',
    description: 'Account | Scan To Pay | Enter Promo Code',
  },
  accountScanToPayScanGiftCodeMessage: {
    defaultMessage: 'Align QR code in the box above & hold steady.',
    description: 'Credit Add Screen | Scanner | Scan Gift Code Message',
  },
  accountScanToPayScanGiftCodeCameraPermissionMessage: {
    defaultMessage: 'Refer to your device’s system settings to enable camera permissions.',
    description: 'Credit Add Screen | Scanner | Camera Permission Message',
  },
  creditAddClose: {
    defaultMessage: 'Close',
    description: 'Credit Add | Close',
  },
  creditAddCancel: {
    defaultMessage: 'Cancel',
    description: 'Credit Add | Cancel',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  padding: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    paddingHorizontal: theme.spacing['4'],
  },
  floatingButton: {
    position: 'absolute',
    padding: theme.spacing['1'],
    top: theme.spacing['4'],
    right: theme.spacing['4'],
    zIndex: theme.zIndex.popover,
    backgroundColor: theme.colors.OPACITY.CREAM.DARKEST,
    borderRadius: theme.radius['3xLarge'],
    ...theme.elevations['4'],
  },
  scanner: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: theme.colors.BLACK,
  },
  scannerMessageContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingVertical: theme.spacing['4'],
    justifyContent: 'space-evenly',
    backgroundColor: theme.colors.APP_BACKGROUND,
    borderTopLeftRadius: theme.radius.medium,
    borderTopRightRadius: theme.radius.medium,
    paddingHorizontal: theme.spacing['4'],
  },
  text: {
    paddingBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type NavigationHeaderRightItems = Parameters<typeof useHeaderRight>[0]['items'];
type PromoCodeScannerProps = Readonly<{
  onPromoCodeScanned: (code: string) => void;
  handleHideScanner: () => void;
}>;
